import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Person,
  ExitToApp,
  Settings,
  Notifications,
  ShoppingCart,
  Business,
  MenuBook,
  Apps,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useRbac } from '../../context/RbacContext';
import { BrandLogo } from '../brand/BrandLogo';
import { BRAND } from '../../config/brand';

const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { hasRole } = useRbac();
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solutionsAnchor, setSolutionsAnchor] = useState<null | HTMLElement>(null);
  const [companyAnchor, setCompanyAnchor] = useState<null | HTMLElement>(null);
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleClose();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isPublicRoute = () => {
    const publicRoutes = ['/', '/about', '/services', '/features', '/contact', '/blog', '/faq', '/portfolio', '/pricing', '/products'];
    return publicRoutes.includes(location.pathname);
  };

  const navigationGroups = [
    {
      label: 'Solutions',
      icon: <Apps fontSize="small" sx={{ mr: 1 }} />,
      items: [
        { text: 'Products', path: '/products' },
        { text: 'Features', path: '/features' },
        { text: 'Pricing', path: '/pricing' },
      ],
    },
    {
      label: 'Company',
      icon: <MenuBook fontSize="small" sx={{ mr: 1 }} />,
      items: [
        { text: 'About', path: '/about' },
        { text: 'Services', path: '/services' },
        { text: 'Portfolio', path: '/portfolio' },
        { text: 'Blog', path: '/blog' },
      ],
    },
  ];
  const topLevelItems = [
    { text: 'Home', path: '/' },
    { text: 'FAQ', path: '/faq' },
    { text: 'Contact', path: '/contact' },
  ];

  const userMenuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { text: 'Profile', icon: <Person />, path: '/profile' },
    { text: 'Settings', icon: <Settings />, path: '/settings' },
  ];

  // Add role-specific menu items
  if (hasRole('customer')) {
    userMenuItems.push({ text: 'Customer Portal', icon: <ShoppingCart />, path: '/customer-portal' });
  }
  if (hasRole('admin')) {
    userMenuItems.push({ text: 'Admin Panel', icon: <Business />, path: '/admin' });
  }

  // Define main and dropdown menu items
  const mainMenuItems = [
    { text: 'Home', path: '/' },
    { text: 'About', path: '/about' },
    { text: 'Services', path: '/services' },
    { text: 'Products', path: '/products' },
    { text: 'Contact', path: '/contact' },
  ];
  const moreMenuItems = [
    { text: 'FAQ', path: '/faq' },
    { text: 'Features', path: '/features' },
    { text: 'Pricing', path: '/pricing' },
    { text: 'Portfolio', path: '/portfolio' },
    { text: 'Blog', path: '/blog' },
  ];

  const drawer = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Monio Production
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {topLevelItems.map((item) => (
          <ListItem 
            button 
            key={item.text}
            onClick={() => {
              navigate(item.path);
              setMobileOpen(false);
            }}
            selected={location.pathname === item.path}
          >
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
      {isAuthenticated && (
        <>
          <Divider />
          <List>
            {userMenuItems.map((item) => (
              <ListItem 
                button 
                key={item.text}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
            <ListItem button onClick={handleLogout}>
              <ListItemIcon><ExitToApp /></ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </List>
        </>
      )}
    </Box>
  );

  const logoUrl = BRAND.images.logo;

  return (
    <>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: theme.zIndex.drawer + 1,
          background: `linear-gradient(135deg, ${BRAND.colors.navyDark} 0%, ${BRAND.colors.navy} 100%)`,
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          {/* Logo section */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, cursor: 'pointer' }} onClick={() => navigate('/') }>
            <BrandLogo height={44} showText={!isMobile} variant="light" />
          </Box>
          {/* Desktop Navigation - Indented to right */}
          {!isMobile && isPublicRoute() && (
            <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
              {mainMenuItems.map((item) => (
                <Button
                  key={item.text}
                  color="inherit"
                  onClick={() => navigate(item.path)}
                  sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                >
                  {item.text}
                </Button>
              ))}
              {/* More Dropdown */}
              <Button
                color="inherit"
                onClick={(e) => setMoreAnchor(e.currentTarget)}
                endIcon={<MenuBook />}
                sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
              >
                More
              </Button>
              <Menu
                anchorEl={moreAnchor}
                open={Boolean(moreAnchor)}
                onClose={() => setMoreAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {moreMenuItems.map((item) => (
                  <MenuItem key={item.text} onClick={() => { navigate(item.path); setMoreAnchor(null); }}>
                    {item.text}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isAuthenticated ? (
              <>
                <IconButton color="inherit">
                  <Notifications />
                </IconButton>
                <IconButton
                  onClick={handleMenu}
                  color="inherit"
                >
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
                    {user?.name?.charAt(0) || 'U'}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  {userMenuItems.map((item) => (
                    <MenuItem 
                      key={item.text}
                      onClick={() => {
                        navigate(item.path);
                        handleClose();
                      }}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </MenuItem>
                  ))}
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon><ExitToApp /></ListItemIcon>
                    <ListItemText primary="Logout" />
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Button 
                  color="inherit" 
                  onClick={() => navigate('/auth/login')}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Login
                </Button>
                <Button 
                  variant="contained" 
                  color="secondary"
                  onClick={() => navigate('/auth/register')}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      color: 'primary.main',
                    },
                  }}
                >
                  Register
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer: add logo at the top */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 260 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, cursor: 'pointer' }} onClick={() => { navigate('/'); setMobileOpen(false); }}>
          <img src={logoUrl} alt="Logo" style={{ height: 36, width: 36, marginRight: 8, borderRadius: 8, background: '#fff' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Monio Production</Typography>
        </Box>
        <Divider />
        <List>
          {mainMenuItems.map((item) => (
            <ListItem 
              button 
              key={item.text}
              onClick={() => { navigate(item.path); setMobileOpen(false); }}
              selected={location.pathname === item.path}
            >
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
          <ListItem>
            <ListItemText primary="More" primaryTypographyProps={{ fontWeight: 'bold' }} />
          </ListItem>
          {moreMenuItems.map((item) => (
            <ListItem 
              button 
              key={item.text}
              onClick={() => { navigate(item.path); setMobileOpen(false); }}
              sx={{ pl: 4 }}
              selected={location.pathname === item.path}
            >
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
        </List>
        {isAuthenticated && (
          <>
            <Divider />
            <List>
              {userMenuItems.map((item) => (
                <ListItem 
                  button 
                  key={item.text}
                  onClick={() => {
                    navigate(item.path);
                    setMobileOpen(false);
                  }}
                  selected={location.pathname === item.path}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItem>
              ))}
              <ListItem button onClick={handleLogout}>
                <ListItemIcon><ExitToApp /></ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItem>
            </List>
          </>
        )}
      </Drawer>

      <Toolbar /> {/* Spacer for fixed AppBar */}
    </>
  );
};

export default Header;
